import { CURRENCIES, DEFAULT_CURRENCY, formatMoney, toMinorUnits, type Cadence } from "@vouch/shared";
import { useEffect, useState } from "react";
import {
  useAccounts,
  useCategories,
  useCreateRecurring,
  useCreateTransaction,
  useCreateTransfer,
  useFxRate,
} from "../lib/hooks";
import { useAuth } from "../lib/auth";
import { toast } from "../lib/toast";
import { Button, Input, Modal, Select } from "./ui";

type Mode = "income" | "expense" | "transfer";

export function QuickAddModal({
  open,
  mode,
  onClose,
}: {
  open: boolean;
  mode: Mode;
  onClose: () => void;
}): JSX.Element {
  const { user } = useAuth();
  const { data: accounts = [] } = useAccounts();
  const { data: categories = [] } = useCategories();
  const createTx = useCreateTransaction();
  const createTransfer = useCreateTransfer();
  const createRecurring = useCreateRecurring();
  const defaultCurrency = user?.defaultCurrency ?? DEFAULT_CURRENCY;

  const [accountId, setAccountId] = useState("");
  const [toAccountId, setToAccountId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState(defaultCurrency);
  const [currencyTouched, setCurrencyTouched] = useState(false);
  const [description, setDescription] = useState("");
  const [repeats, setRepeats] = useState(false);
  const [cadence, setCadence] = useState<Cadence>("monthly");
  const [dayOfMonth, setDayOfMonth] = useState("1");
  const [error, setError] = useState<string | null>(null);

  const title = mode === "income" ? "Add income" : mode === "expense" ? "Add expense" : "Transfer";
  const busy = createTx.isPending || createTransfer.isPending || createRecurring.isPending;
  const typeCategories = categories.filter((c) => c.kind === mode);
  const selectedAccount = accounts.find((a) => a.id === accountId);
  const selectedToAccount = accounts.find((a) => a.id === toAccountId);
  const sourceAccountCurrency = selectedAccount?.currency ?? currency;
  const destinationAccountCurrency = selectedToAccount?.currency ?? currency;
  const amountMinor = toMinorUnits(Number(amount));

  const sourceFx = useFxRate(currency, sourceAccountCurrency);
  const destinationFx = useFxRate(currency, destinationAccountCurrency);
  const sourceImpact = convertMinorForPreview(amountMinor, currency, sourceAccountCurrency, sourceFx.data);
  const destinationImpact = convertMinorForPreview(
    amountMinor,
    currency,
    destinationAccountCurrency,
    destinationFx.data,
  );
  const needsSourceFx = Boolean(selectedAccount && currency !== sourceAccountCurrency);
  const needsDestinationFx = Boolean(mode === "transfer" && selectedToAccount && currency !== destinationAccountCurrency);
  const fxLoading = (needsSourceFx && sourceFx.isFetching) || (needsDestinationFx && destinationFx.isFetching);
  const fxMissing =
    (needsSourceFx && sourceFx.data == null && !sourceFx.isFetching) ||
    (needsDestinationFx && destinationFx.data == null && !destinationFx.isFetching);

  useEffect(() => {
    if (!open) return;
    if (!accountId && accounts[0]) setAccountId(accounts[0].id);
  }, [accountId, accounts, open]);

  useEffect(() => {
    if (!open || currencyTouched) return;
    setCurrency(selectedAccount?.currency ?? defaultCurrency);
  }, [currencyTouched, defaultCurrency, open, selectedAccount?.currency]);

  const reset = (): void => {
    setAmount("");
    setDescription("");
    setCategoryId("");
    setRepeats(false);
    setCurrencyTouched(false);
    setError(null);
  };

  const submit = async (): Promise<void> => {
    setError(null);
    if (!accountId || !Number.isFinite(amountMinor) || amountMinor <= 0) {
      setError("Pick an account and a positive amount.");
      return;
    }
    if (fxLoading) {
      setError("Still fetching the live exchange rate.");
      return;
    }
    if (fxMissing) {
      setError("Live exchange rate is unavailable. Try again in a moment.");
      return;
    }
    try {
      if (mode === "transfer") {
        if (!toAccountId || toAccountId === accountId) {
          setError("Pick a different destination account.");
          return;
        }
        await createTransfer.mutateAsync({
          senderAccountId: accountId,
          receiverAccountId: toAccountId,
          amountMinor,
          currency,
          description: description || undefined,
        });
        toast.success("Transfer recorded");
      } else {
        await createTx.mutateAsync({
          type: mode,
          accountId,
          amountMinor,
          currency,
          categoryId: categoryId || undefined,
          description: description || undefined,
        });
        if (repeats) {
          await createRecurring.mutateAsync({
            kind: mode,
            amountMinor: sourceImpact ?? amountMinor,
            accountId,
            categoryId: categoryId || undefined,
            cadence,
            dayOfMonth: Number(dayOfMonth) || undefined,
            description: description || undefined,
          });
          toast.success(`${mode === "income" ? "Income" : "Expense"} saved & set to repeat`);
        } else {
          toast.success(mode === "income" ? "Income added" : "Expense added");
        }
      }
      reset();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong");
    }
  };

  return (
    <Modal open={open} title={title} onClose={onClose}>
      <div className="space-y-3">
        <Select
          label={mode === "transfer" ? "From account" : "Account"}
          value={accountId}
          onChange={(e) => setAccountId(e.target.value)}
        >
          <option value="">Select…</option>
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name} · {a.currency}
            </option>
          ))}
        </Select>

        {mode === "transfer" && (
          <Select label="To account" value={toAccountId} onChange={(e) => setToAccountId(e.target.value)}>
            <option value="">Select…</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name} · {a.currency}
              </option>
            ))}
          </Select>
        )}

        {mode !== "transfer" && (
          <Select label="Type / category" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
            <option value="">Uncategorised</option>
            {typeCategories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        )}

        <div className="grid grid-cols-[minmax(0,1fr)_7rem] gap-2">
          <Input
            label="Amount"
            type="number"
            min="0"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.00"
          />
          <Select
            label="Currency"
            value={currency}
            onChange={(e) => {
              setCurrency(e.target.value);
              setCurrencyTouched(true);
            }}
          >
            {CURRENCIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>
        </div>

        {selectedAccount && Number.isFinite(amountMinor) && amountMinor > 0 && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500">
            {fxLoading ? (
              "Fetching live exchange rate…"
            ) : fxMissing ? (
              "Live exchange rate is unavailable right now."
            ) : mode === "transfer" && selectedToAccount ? (
              <>
                Account impact: debit{" "}
                <strong className="text-slate-700">
                  {formatMoney(sourceImpact ?? amountMinor, sourceAccountCurrency)}
                </strong>{" "}
                from {selectedAccount.name}; credit{" "}
                <strong className="text-slate-700">
                  {formatMoney(destinationImpact ?? amountMinor, destinationAccountCurrency)}
                </strong>{" "}
                to {selectedToAccount.name}.
              </>
            ) : (
              <>
                Account impact:{" "}
                <strong className="text-slate-700">
                  {formatMoney(sourceImpact ?? amountMinor, sourceAccountCurrency)}
                </strong>{" "}
                in {selectedAccount.name}.
              </>
            )}
            {repeats && mode !== "transfer" && currency !== sourceAccountCurrency && sourceImpact != null && (
              <p className="mt-1">
                Recurring rule will repeat the account impact in {sourceAccountCurrency}.
              </p>
            )}
          </div>
        )}

        <Input
          label="Description (optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />

        {mode !== "transfer" && (
          <div className="rounded-lg border border-slate-200 p-3">
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <input type="checkbox" checked={repeats} onChange={(e) => setRepeats(e.target.checked)} />
              Repeats (e.g. rent, salary)
            </label>
            {repeats && (
              <div className="mt-3 grid grid-cols-2 gap-2">
                <Select label="Frequency" value={cadence} onChange={(e) => setCadence(e.target.value as Cadence)}>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="yearly">Yearly</option>
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Every 2 weeks</option>
                </Select>
                <Input
                  label="Day of month"
                  type="number"
                  min="1"
                  max="31"
                  value={dayOfMonth}
                  onChange={(e) => setDayOfMonth(e.target.value)}
                />
                <p className="col-span-2 text-xs text-slate-400">
                  Future months show this as a pending item you mark done.
                </p>
              </div>
            )}
          </div>
        )}

        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} disabled={busy}>
            {busy ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function convertMinorForPreview(
  amountMinor: number,
  fromCurrency: string,
  toCurrency: string,
  rate: number | null | undefined,
): number | null {
  if (!Number.isFinite(amountMinor) || amountMinor <= 0) return null;
  if (fromCurrency === toCurrency) return amountMinor;
  if (rate == null) return null;
  return Math.round(amountMinor * rate);
}
